import { Button, DatePicker, Form, Space } from "antd";
import dayjs from "dayjs";
import { useEffect } from "react";
import DateIcon from "../../assets/icons/DateIcon";
import CustomModal from "../../components/ui/CustomModal";
import { useSendSalaryReportMutation } from "../../features/payroll/payrollApi";
import useError from "../../hooks/useError";
import useModal from "../../hooks/useModal";
import { toast } from "../../lib/toast";
import { ModalComponentProps } from "../../types";
import ConfirmModal from "../ui/ConfirmModal";

export default function SalaryDisbursement({
  handleClose,
  ...rest
}: ModalComponentProps) {
  const [form] = Form.useForm();
  const { open, handleOpen, handleClose: handleCClose } = useModal();
  const [sendSalaryReport, { isLoading, isError, error, isSuccess }] =
    useSendSalaryReportMutation();
  useError(isError, error, "Salary disbursement fail");

  const onFinish = (values: any) => {
    const { month_year } = values;

    const newValues = {
      month_year: month_year?.format("MM-YYYY"),
    };

    sendSalaryReport(newValues);
  };

  const handleDownload = () => {
    const data = open?.data;

    // Format into readable text
    const content = (data as unknown as any[])
      ?.map(
        (item) =>
          `Name: ${item.employeeName}\nEmail: ${
            item.employeeEmail
          }\nMissing Fields:\n- ${item.missingFields.join("\n- ")}\n`
      )
      .join("\n----------------------\n\n");

    // Create and download file
    const blob = new Blob([content], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "error-log.txt";
    link.click();
    URL.revokeObjectURL(url);
  };

  useEffect(() => {
    if (isSuccess) {
      toast("success", "Salary disbursement successfully");
      handleClose(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccess]);

  useEffect(() => {
    if (
      isError &&
      (error as { data: { data: any } })?.data?.data?.employeesWithMissingInfo
        ?.length
    ) {
      handleOpen({
        type: "VIEW",
        data: (error as { data: { data: any } })?.data?.data
          ?.employeesWithMissingInfo,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isError, error]);

  return (
    <CustomModal
      title="Salary Disbursement"
      handleClose={handleClose}
      {...rest}
      footer={null}
      width={500}
    >
      <Form
        className="custom-form"
        layout="vertical"
        requiredMark="optional"
        size="large"
        form={form}
        onFinish={onFinish}
      >
        <Form.Item
          name="month_year"
          label="Select Month"
          className="mb-4"
          rules={[
            {
              required: true,
              message: "Please select month",
            },
          ]}
        >
          <DatePicker
            format="MMMM YYYY"
            suffixIcon={<DateIcon width={18} height={20} />}
            style={{ width: "100%" }}
            size="large"
            picker="month"
            disabledDate={(current) => current && current.isAfter(dayjs())}
          />
        </Form.Item>

        <Space className="w-full pt-2 sticky bottom-0 bg-white" size={12}>
          <Button
            className="normal-dark-button modal-button"
            onClick={handleClose}
            size="middle"
          >
            Cancel
          </Button>
          <Button
            className="modal-button"
            type="primary"
            size="middle"
            htmlType="submit"
            loading={isLoading}
          >
            Submit
          </Button>
        </Space>
      </Form>

      {
        <ConfirmModal
          open={open?.open}
          handleClose={handleCClose}
          handleOk={handleDownload}
          confirmTitle="Error Detected"
          questionText={(error as { data: { message: string } })?.data?.message}
          okText="Download"
        />
      }
    </CustomModal>
  );
}
